import { useState } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import * as Menu from '@radix-ui/react-dropdown-menu';
import type { AdminTarget } from '@dunea/plugin-sdk';
import { classNames } from '~/utils/classNames';
import { BTN_GHOST } from '~/components/workbench/tools/payments/ui';
import { ListingIcon } from '~/components/workbench/tools/marketplace/parts';
import { AdminExtensionFrame } from './AdminExtensionFrame';
import { useSlotEntries } from './AdminExtensionSlot';
import type { SlotEntry } from './admin-extensions-model';

/**
 * The actions menu of a resource (or of a selection, for bulk targets):
 * Dunea's own actions first, then installed plugins' actions. Choosing one
 * opens a modal: first-party actions render their component, plugin actions
 * a sandboxed iframe opened for (target, resourceId). Renders nothing when
 * there are no actions.
 *
 * `resourceId` null = nothing selected (bulk): the menu shows but is disabled.
 */
export function AdminActionsMenu({
  appId,
  target,
  resourceId,
  label = 'More actions',
  disabledHint = 'Select something first',
  compact = false,
}: {
  appId: string;
  target: AdminTarget;
  resourceId?: string | null;
  label?: string;
  disabledHint?: string;
  /** Icon-only trigger (table rows). */
  compact?: boolean;
}) {
  const entries = useSlotEntries(appId, target);
  const [open, setOpen] = useState<SlotEntry | null>(null);
  const disabled = resourceId === null;

  if (entries.length === 0) {
    return null;
  }

  return (
    <>
      <Menu.Root>
        <Menu.Trigger asChild disabled={disabled}>
          <button
            type="button"
            className={classNames(BTN_GHOST, compact && 'px-1.5')}
            title={disabled ? disabledHint : label}
            aria-label={label}
          >
            <div className="i-ph:dots-three-outline-fill text-[13px]" />
            {!compact && <span>{label}</span>}
          </button>
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Content
            align="end"
            sideOffset={6}
            collisionPadding={8}
            className="z-[9999] min-w-[200px] max-w-[280px] rounded-lg border border-hairline bg-raised p-1 shadow-[0_24px_48px_-24px_rgba(0,0,0,0.9)]"
          >
            {entries.map((entry) => (
              <Menu.Item
                key={entry.key}
                onSelect={() => setOpen(entry)}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-[12px] text-primary outline-none data-[highlighted]:bg-sunken"
              >
                {entry.source === 'plugin' ? (
                  <ListingIcon url={entry.extension.iconUrl} name={entry.extension.name} size={14} />
                ) : (
                  <div className="i-ph:lightning text-[13px] text-muted" />
                )}
                <span className="min-w-0 flex-1 truncate">{entry.title}</span>
                {entry.source === 'plugin' && (
                  <span className="max-w-[90px] truncate text-[10.5px] text-muted">{entry.extension.name}</span>
                )}
              </Menu.Item>
            ))}
          </Menu.Content>
        </Menu.Portal>
      </Menu.Root>

      <RadixDialog.Root open={open !== null} onOpenChange={(next) => !next && setOpen(null)}>
        <RadixDialog.Portal>
          <RadixDialog.Overlay className="fixed inset-0 z-[9999] bg-black/75 backdrop-blur-sm" />
          <RadixDialog.Content
            className="ui-modal fixed left-1/2 top-1/2 z-[9999] flex h-[min(640px,calc(100dvh-24px))] w-[calc(100vw-24px)] max-w-[640px] -translate-x-1/2 -translate-y-1/2 flex-col focus:outline-none"
            aria-describedby={undefined}
            // Radix catches Escape in the document capture phase; stopping it there keeps
            // it from reaching window listeners underneath (the order drawer), so only
            // this modal closes.
            onEscapeKeyDown={(e) => e.stopPropagation()}
          >
            {open && (
              <>
                <div className="flex items-center gap-2 border-b border-hairline px-4 py-3">
                  {open.source === 'plugin' && (
                    <ListingIcon url={open.extension.iconUrl} name={open.extension.name} size={18} />
                  )}
                  <RadixDialog.Title className="min-w-0 truncate text-[13px] font-medium text-primary">
                    {open.title}
                  </RadixDialog.Title>
                  {open.source === 'plugin' && (
                    <span className="min-w-0 truncate text-[11px] text-muted">{open.extension.name}</span>
                  )}
                  <div className="flex-1" />
                  <RadixDialog.Close asChild>
                    <button type="button" className="ui-btn-icon" aria-label="Close">
                      <div className="i-ph:x text-[13px]" />
                    </button>
                  </RadixDialog.Close>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto">
                  {open.source === 'plugin' ? (
                    <AdminExtensionFrame
                      appId={appId}
                      extension={open.extension}
                      resourceId={resourceId ?? undefined}
                      mode="modal"
                      onClose={() => setOpen(null)}
                    />
                  ) : (
                    <div className="p-4">
                      <open.card.Component appId={appId} target={target} resourceId={resourceId ?? undefined} />
                    </div>
                  )}
                </div>
              </>
            )}
          </RadixDialog.Content>
        </RadixDialog.Portal>
      </RadixDialog.Root>
    </>
  );
}
